import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthContext, UserProfile, LoginRequest, PlatformRole } from '../types';
import { apiClient } from '../services/apiClient';
import { adminService } from '../services/admin/adminService';
import { queryClient } from '../services/queryClient';

// localStorage keys used to stash the admin's own session while impersonating.
const ADMIN_TOKEN_KEY = 'admin_token';
const ADMIN_REFRESH_KEY = 'admin_refresh_token';
const ADMIN_PROFILE_KEY = 'admin_user_profile';

// The backend (golang-jwt/jwt/v5) encodes JWTs with base64url (RFC 7519), which
// uses '-'/'_' instead of '+'/'/' and strips padding. Raw atob() only understands
// standard base64 and throws on any payload containing those characters — decode
// through this helper everywhere a JWT payload is parsed instead of calling
// atob() directly.
const decodeJwtPayload = (token: string): any => {
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) return {};
  let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) base64 += '=';
  return JSON.parse(atob(base64));
};

// Build a UserProfile from a token + a base profile object, decoding the JWT
// for the fields that only live in the token (organization_slug, roles, ...).
const buildProfileFromToken = (token: string, base: Partial<UserProfile>): UserProfile => {
  let jwtPayload: any = {};
  try {
    jwtPayload = decodeJwtPayload(token);
  } catch (e) {
    console.warn('Failed to decode JWT:', e);
  }
  return {
    id: base.id ?? jwtPayload.sub,
    name: base.name ?? jwtPayload.name,
    email: base.email ?? jwtPayload.email,
    user_type: (base.user_type ?? jwtPayload.user_type) as 'platform_user' | 'org_user',
    enabled: base.enabled ?? true,
    created_at: base.created_at ?? new Date().toISOString(),
    platform_roles: (base.platform_roles ?? []) as PlatformRole[],
    organization_slug: base.organization_slug ?? jwtPayload.organization_slug,
    permissions: jwtPayload.permissions || [],
    organization_roles: jwtPayload.orgRoles || [],
    roles: jwtPayload.roles || [],
  } as UserProfile;
};

const AuthContextInstance = createContext<AuthContext | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState<boolean>(
    () => !!localStorage.getItem(ADMIN_TOKEN_KEY)
  );

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('auth_token');
      const storedUser = localStorage.getItem('user_profile');

      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          
          // Parse stored user and enhance with JWT token data
          let parsedUser = JSON.parse(storedUser);
          
          // Decode JWT to get missing fields like organization_slug
          try {
            const tokenParts = storedToken.split('.');
            if (tokenParts.length === 3 && tokenParts[1]) {
              const jwtPayload = decodeJwtPayload(storedToken);

              // Enhance user profile with JWT data
              const jwtUserType = jwtPayload.user_type || parsedUser.user_type;
              parsedUser = {
                ...parsedUser,
                user_type: jwtUserType, // Extract user_type from JWT
                // platform_roles is the authoritative gate for platform admin UI;
                // fall back to the JWT roles claim when it is missing from storage.
                platform_roles:
                  parsedUser.platform_roles?.length
                    ? parsedUser.platform_roles
                    : (jwtUserType === 'platform_user' ? jwtPayload.roles || [] : []),
                organization_slug: jwtPayload.organization_slug,
                permissions: jwtPayload.permissions || [],
                organization_roles: jwtPayload.orgRoles || [], // Keep for backward compatibility
                roles: jwtPayload.roles || [], // Add unified roles field
              };
            }
          } catch (jwtError) {
            console.warn('Failed to decode JWT during initialization:', jwtError);
          }
          
          setUser(parsedUser);
          
          // Fetch fresh permissions
          const userPermissions = await fetchUserPermissions();
          setPermissions(userPermissions);
        } catch (error) {
          console.error('Failed to initialize auth:', error);
          logout();
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  // Auto-refresh token — never refresh an impersonation session (refreshing
  // would re-mint a 24h token that strips the impersonated_by claim). Let it
  // expire naturally (~1h) instead.
  useEffect(() => {
    if (!token || isImpersonating) return;

    const interval = setInterval(() => {
      refreshToken().catch(() => {
        console.warn('Token refresh failed, redirecting to login');
        forceReLogin();
      });
    }, 15 * 60 * 1000); // 15 minutes

    return () => clearInterval(interval);
  }, [token, isImpersonating]);

  // Listen for logout events from apiClient (e.g. a 401). If we're impersonating
  // (impersonation token expired), fall back to the stashed admin session
  // instead of logging out completely.
  useEffect(() => {
    // Both apiClient and the react-query cache can dispatch auth:logout for the
    // same burst of 401s. Handle only the first — otherwise the second run finds
    // the admin stash already consumed by restoreAdminSession and wrongly
    // forceReLogin()s the user we just restored.
    let handling = false;
    const handleLogout = () => {
      if (handling) return;
      handling = true;
      setTimeout(() => { handling = false; }, 3000);
      if (localStorage.getItem(ADMIN_TOKEN_KEY)) {
        // Impersonation session ended (e.g. token expired, refresh không áp dụng cho token
        // impersonation) → revert to admin, rồi đưa admin về đúng chỗ họ bắt đầu ("Login As"),
        // khớp hành vi của nhánh 403 (auth:impersonation-forbidden) phía dưới.
        restoreAdminSession();
        if (window.location.pathname !== '/admin/users') {
          window.location.assign('/admin/users');
        }
      } else {
        // No session to fall back to → send the user to login to re-authenticate.
        forceReLogin();
      }
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  // Any 403 while impersonating (dispatched by apiClient/the generated API
  // client) → the impersonated session hit something it can't access. Exit
  // impersonation and send the admin back to where they started ("Login As").
  useEffect(() => {
    let handling = false;
    const handleForbidden = () => {
      if (handling) return;
      handling = true;
      setTimeout(() => { handling = false; }, 3000);
      if (localStorage.getItem(ADMIN_TOKEN_KEY)) {
        restoreAdminSession();
        if (window.location.pathname !== '/admin/users') {
          window.location.assign('/admin/users');
        }
      }
    };

    window.addEventListener('auth:impersonation-forbidden', handleForbidden);
    return () => window.removeEventListener('auth:impersonation-forbidden', handleForbidden);
  }, []);

  const fetchUserPermissions = async (): Promise<string[]> => {
    try {
      const response = await apiClient.get<{
        user_id: string;
        user_type: string;
        organizations: string[];
        platform_roles: string[];
        org_roles: string[];
        permissions: string[];
        global_access: boolean;
      }>('/auth/permissions');
      return response.permissions || [];
    } catch (error) {
      console.error('Failed to fetch user permissions:', error);
      return [];
    }
  };

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const loginData: LoginRequest = { email, password };
      const response = await apiClient.post<{
        access_token: string;
        refresh_token: string;
        user: {
          id: string;
          email: string;
          name: string;
          user_type: string;
          organization_slug: string;
          platform_roles: string[];
          enabled: boolean;
        };
        expires_in: number;
        message: string;
      }>('/auth/login', loginData);
      
      // Decode JWT to get additional user information
      let jwtPayload: any = {};
      try {
        jwtPayload = decodeJwtPayload(response.access_token);
      } catch (jwtError) {
        console.warn('Failed to decode JWT during login:', jwtError);
      }

      // Map API response to UserProfile format. The backend sends snake_case
      // user fields; the JWT `roles` claim is the authoritative role source.
      const userType = (response.user.user_type || jwtPayload.user_type) as 'platform_user' | 'org_user';
      const platformRoles = (response.user.platform_roles
        || (userType === 'platform_user' ? jwtPayload.roles : [])
        || []) as PlatformRole[];

      const userProfile: UserProfile = {
        id: response.user.id,
        name: response.user.name,
        email: response.user.email,
        user_type: userType,
        enabled: response.user.enabled,
        created_at: new Date().toISOString(),
        platform_roles: platformRoles,
        organization_slug: jwtPayload.organization_slug,
        permissions: jwtPayload.permissions || [],
        organization_roles: jwtPayload.orgRoles || [], // Keep for backward compatibility
        roles: jwtPayload.roles || [], // Add unified roles field
      };

      setUser(userProfile);
      setToken(response.access_token);
      // Get permissions from user token or set default based on roles
      const defaultPermissions = platformRoles?.includes('super_admin')
        ? ['admin:all']
        : [];
      setPermissions(defaultPermissions);
      
      // Store in localStorage
      localStorage.setItem('auth_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      localStorage.setItem('user_profile', JSON.stringify(userProfile));
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = (): void => {
    setUser(null);
    setToken(null);
    setPermissions([]);

    // Clear localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_profile');
    localStorage.removeItem('refresh_token');
    // Also clear any stashed impersonation session
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_REFRESH_KEY);
    localStorage.removeItem(ADMIN_PROFILE_KEY);

    // Clear API client tenant context
    apiClient.setTenantContext(null as any);
  };

  // Session can no longer be refreshed → clear everything and send the user to
  // the login page so they can re-authenticate. Hard redirect so it works
  // regardless of the current router context (and avoids a redirect loop).
  const forceReLogin = (): void => {
    logout();
    if (window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
  };

  const refreshToken = async (): Promise<void> => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiClient.post<{
        access_token: string;
        refresh_token: string;
        expires_in: number;
        message: string;
      }>('/auth/refresh', {
        refresh_token: refreshToken,
      });

      setToken(response.access_token);
      
      // Update localStorage
      localStorage.setItem('auth_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    }
  };

  // Start impersonating a user ("login as"). Stashes the admin session, swaps in
  // the impersonation token, and returns the target profile for redirect.
  const impersonate = async (userId: string): Promise<UserProfile> => {
    if (localStorage.getItem(ADMIN_TOKEN_KEY)) {
      throw new Error('Already impersonating a user');
    }

    const resp = await adminService.impersonateUser(userId);

    // Stash the current (admin) session so we can restore it on exit.
    const currentToken = localStorage.getItem('auth_token');
    const currentRefresh = localStorage.getItem('refresh_token');
    const currentProfile = localStorage.getItem('user_profile');
    if (currentToken) localStorage.setItem(ADMIN_TOKEN_KEY, currentToken);
    if (currentRefresh) localStorage.setItem(ADMIN_REFRESH_KEY, currentRefresh);
    if (currentProfile) localStorage.setItem(ADMIN_PROFILE_KEY, currentProfile);

    const targetProfile = buildProfileFromToken(resp.access_token, {
      id: resp.user.id,
      name: resp.user.name,
      email: resp.user.email,
      user_type: resp.user.user_type as 'platform_user' | 'org_user',
      enabled: resp.user.enabled,
      platform_roles: (resp.user.platform_roles || []) as PlatformRole[],
      // Prefer the API response's organization_slug over decoding it from the
      // JWT — reliable even if the token payload is ever hard to parse.
      organization_slug: resp.user.organization_slug,
    });

    setUser(targetProfile);
    setToken(resp.access_token);
    setPermissions([]);
    setIsImpersonating(true);

    localStorage.setItem('auth_token', resp.access_token);
    localStorage.setItem('refresh_token', resp.refresh_token);
    localStorage.setItem('user_profile', JSON.stringify(targetProfile));

    // Refresh permissions for the impersonated user (best-effort).
    fetchUserPermissions().then(setPermissions).catch(() => undefined);

    return targetProfile;
  };

  // Restore the admin session from the stashed keys (used on exit and on
  // impersonation-token expiry).
  const restoreAdminSession = async (): Promise<void> => {
    const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    const adminRefresh = localStorage.getItem(ADMIN_REFRESH_KEY);
    const adminProfile = localStorage.getItem(ADMIN_PROFILE_KEY);

    if (!adminToken) {
      logout();
      return;
    }

    localStorage.setItem('auth_token', adminToken);
    if (adminRefresh) localStorage.setItem('refresh_token', adminRefresh);
    if (adminProfile) localStorage.setItem('user_profile', adminProfile);

    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_REFRESH_KEY);
    localStorage.removeItem(ADMIN_PROFILE_KEY);

    // Auto-refresh bị tắt suốt lúc đang impersonate (xem effect phía trên) nên token admin đã stash
    // bị đóng băng từ lúc bắt đầu "Login As" — nếu phiên impersonation kéo dài hơn hạn token gốc,
    // token admin CŨNG đã hết hạn. Chủ động refresh bằng refresh_token của admin trước khi dùng lại,
    // tránh vừa restore xong lại 401 ngay lập tức. Refresh lỗi thì vẫn dùng tạm token đã stash — còn
    // hơn không, và request kế tiếp sẽ tự 401→refresh lại qua luồng apiClient bình thường.
    try {
      await refreshToken();
    } catch (e) {
      console.warn('Refresh token admin gốc lúc restore thất bại, dùng tạm token đã stash:', e);
    }

    const restoredToken = localStorage.getItem('auth_token') || adminToken;
    let base: Partial<UserProfile> = {};
    try {
      if (adminProfile) base = JSON.parse(adminProfile);
    } catch {
      base = {};
    }
    const adminUser = buildProfileFromToken(restoredToken, base);

    setUser(adminUser);
    setToken(restoredToken);
    setIsImpersonating(false);
    fetchUserPermissions().then(setPermissions).catch(() => setPermissions([]));
    // Refetch every active query with the restored admin token so the UI recovers
    // in place — without this the queries that 401'd stay errored until a reload.
    queryClient.invalidateQueries();
  };

  // Stop impersonating: audit the stop (best-effort, while the impersonation
  // token is still active), then restore the admin session.
  const stopImpersonation = async (): Promise<void> => {
    try {
      await adminService.stopImpersonation();
    } catch (e) {
      console.warn('Failed to record impersonation stop:', e);
    }
    await restoreAdminSession();
  };

  const value: AuthContext = {
    user,
    token,
    permissions,
    isAuthenticated: !!user && !!token,
    isLoading,
    login,
    logout,
    refreshToken,
    impersonate,
    stopImpersonation,
    isImpersonating,
  };

  // Show loading spinner while initializing
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        Loading...
      </div>
    );
  }

  return (
    <AuthContextInstance.Provider value={value}>
      {children}
    </AuthContextInstance.Provider>
  );
};

export const useAuth = (): AuthContext => {
  const context = useContext(AuthContextInstance);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};